import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  listRevisionGroups,
  resolveLiveRevisionGroupsAsOneUndo,
  resolveRevisionsAsOneUndo,
  RevisionGroupItem
} from '../../../../utils/documentEditorPrimitives';
import { isRobinEditing } from '../robinActivity';
import { featheryWindow } from '../../../../utils/browser';
import { cardResolverFor } from '../../../../robin/document/mounts';
import { withTocRefresh } from '../toc/refreshToc';
import { redoDocument, undoDocument } from '../historyRouting';
import { isOpeningDocument, setActiveInlineRevisions } from '../useDocxEditor';
import BookmarkTab from './BookmarkTab';
import RailHead from './RailHead';
import GroupCard from './GroupCard';
import { ChipView, GroupView } from './types';
import { ACCENT_LINE, INK, PANEL } from './styles';
import type { TrackedChangeAcceptance } from '../history/useDocxHistorySession';

// Review rail for pending tracked changes: one card per assistant change set
// (plus one per human author), expanding to −/+ diff "chips" with
// per-chip, per-card and rail-wide resolution — all through the
// non-cascading path as ONE undo unit. Resolved edits leave the rail;
// an undo brings them back via the contentChange refresh.
//
// This file owns all state and editor wiring; BookmarkTab / RailHead /
// GroupCard / ChangeChip are presentational.

interface Props {
  editor: any;
  /** Host-owned visibility (drawer handle, ✕, inline click all agree);
   *  stays mounted while hidden so the listeners keep running. */
  hidden?: boolean;
  onHiddenChange?: (hidden: boolean) => void;
  /** Reports the pending tracked-change count whenever it changes. */
  onPendingCountChange?: (count: number) => void;
  /** Persists an accept operation as a distinct confirmed history version. */
  onAcceptTrackedChanges?: (
    acceptance: TrackedChangeAcceptance,
    accept: () => void
  ) => Promise<void>;
}

// contentChange fires once per keystroke; one trailing refresh after typing
// pauses is enough for the rail (refresh walks every revision + getRange).
const CONTENT_REFRESH_DEBOUNCE_MS = 150;

const groupKeyOf = (changeSetId: string, group: string) =>
  `${changeSetId} ${group}`;

// Forces a real paint before `fn` runs. A single setTimeout(0) doesn't: the
// browser coalesces the state-update paint with whatever runs in the same
// frame, so the spinner never reached the screen for a fast resolve.
function afterNextPaint(fn: () => void): void {
  const win = featheryWindow() as any;
  if (typeof win.requestAnimationFrame !== 'function') {
    setTimeout(fn, 0);
    return;
  }
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    fn();
  };
  win.requestAnimationFrame(() => win.requestAnimationFrame(run));
  // Hidden tabs suspend rAF entirely; the timeout guarantees the deferred
  // work still runs (a hidden tab has no paint to wait for anyway).
  setTimeout(run, 100);
}

// The single containment boundary for editor faults in this file: one guard
// at each UI/EJ2 event entry (render/effect faults go to the host's
// RailErrorBoundary instead). Everything beneath it - reads, refreshes,
// resolution calls - throws normally, so a mid-operation failure surfaces
// here rather than being swallowed at its call site. Contained errors log at
// the browser's verbose/debug level: teardown noise stays out of production
// logs, but a RECURRING failure - a real regression - stays visible.
const reportEditorEventError = (error: unknown) =>
  console.debug('Feathery: tracked-changes rail editor call failed.', error);

const handleEditorEvent = (fn: () => void | Promise<void>) => {
  try {
    const result = fn();
    if (result && typeof result.catch === 'function')
      result.catch(reportEditorEventError);
  } catch (error) {
    reportEditorEventError(error);
  }
};

// An instance on its way out is an EXPECTED state, not a fault: EJ2 throws on
// any touch of a destroyed editor, and the destroy can land between an
// isDestroyed check and the read after it. Such a read means "no editor" and
// nothing more. Anything else - a real fault in a read of a LIVE editor - is a
// programming error and must still reach the boundary, which is why this asks
// the instance rather than swallowing every failure here.
const isTornDown = (editor: any): boolean => {
  try {
    return !editor || editor.isDestroyed === true || !editor.revisions;
  } catch {
    return true;
  }
};

// 'update-premium-2026' -> 'Update premium 2026'. The id is the assistant's
// own kebab/snake label; render it as a title rather than as code.
const humanizeGroupId = (id: string) => {
  const spaced = id.replace(/[-_]+/g, ' ').trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : id;
};

/**
 * What the rail says when a resolve could not finish.
 *
 * The pending counter cannot carry this on its own: a card that applied a prefix
 * and stalled just redraws a smaller number, which reads as success. Plain
 * language, an exact count, and the one action that undoes the partial state -
 * the whole resolve was a single undo unit, so one step back is the way out.
 */
const stallNotice = (
  unresolved: number,
  resolved: number,
  isAccept: boolean
): string => {
  const action = isAccept ? 'accepted' : 'rejected';
  if (!resolved)
    return `${unresolved} ${
      unresolved === 1 ? 'edit' : 'edits'
    } could not be ${action}. Nothing in this change was resolved.`;
  return `${resolved} ${
    resolved === 1 ? 'edit was' : 'edits were'
  } ${action}, but ${unresolved} ${
    unresolved === 1 ? 'edit remains' : 'edits remain'
  }. Review the document before retrying.`;
};

// A chip is one EDIT, and an edit is a paragraph's worth of change backed by
// however many revisions SyncFusion authored for it - its runs, its paragraph
// mark, and for a replace both halves. Every resolve path must settle all of
// them with the one decision, or the chip's own paragraph boundary can be
// resolved the opposite way from its text.
const chipRevisions = (chip: ChipView) => [
  ...(chip.revisions ?? [chip.revision]),
  ...(chip.partnerRevisions ?? (chip.partner ? [chip.partner] : []))
];

/**
 * The first chip a person can see: one whose text, before or after, holds more
 * than page breaks, paragraph marks and whitespace. A card can open with a page
 * break or an empty paragraph, and going there shows nothing; the first chip
 * when none is visible.
 */
const firstVisibleChip = (chips: ChipView[]): ChipView =>
  chips.find((chip) =>
    /[^\s\ufffc]/.test(`${chip.text ?? ''}${chip.beforeText ?? ''}`)
  ) ?? chips[0];

const itemRevisions = (item: RevisionGroupItem) => [
  ...item.revisions,
  ...(item.partnerRevisions ?? [])
];

function TrackedChangeGroups({
  editor,
  hidden,
  onHiddenChange,
  onPendingCountChange,
  onAcceptTrackedChanges
}: Props) {
  // Only live (pending) revisions render; a resolved edit disappears from
  // the rail and reappears if the resolution is undone.
  const [groups, setGroups] = useState<GroupView[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Which bulk action is running, for RailHead's spinner. The resolve loop
  // is synchronous, so it's deferred past a paint (see afterNextPaint).
  const [resolvingAll, setResolvingAll] = useState<'accept' | 'reject' | null>(
    null
  );
  // The edits ringed in the document — a group-title click rings the whole
  // group. First entry is primary: what stepping and scroll-into-view use.
  const [activeRevisions, setActiveRevisions] = useState<any[]>([]);
  // Keyboard stepping reads this ref, not state: selectRevision echoes a
  // synchronous selectionChange that can resolve to a NEIGHBOUR and make
  // every arrow press skip an edit.
  const activeRevisionRef = useRef<any>(null);
  const ignoreSelectionRef = useRef(false);
  const resolvingRef = useRef(false);
  // Survives the refresh that follows the resolve, and only that: the next
  // resolve replaces it, and a clean one clears it.
  const [stall, setStall] = useState<string | null>(null);
  const rowRefs = useRef(new Map<any, HTMLDivElement>());
  // Group card elements, keyed by group.key — the scroll effect aligns to a
  // card's TOP (header included) instead of its first chip's row.
  const groupRefs = useRef(new Map<string, HTMLDivElement>());
  // The primary revision the rail last scrolled to; lets the scroll effect
  // no-op on renders that didn't change the selection (e.g. expand/collapse).
  const lastScrolledPrimaryRef = useRef<any>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollBoxRef = useRef<HTMLDivElement>(null);

  // Syncfusion steals focus into its editable div on every selection change;
  // panel actions must take it back or the next J/K press moves the DOCUMENT
  // CARET instead of stepping chips.
  const refocusPanel = () => {
    panelRef.current?.focus({ preventScroll: true });
  };

  const commitActiveRevisions = useCallback(
    (revisions: any[]) => {
      activeRevisionRef.current = revisions[0] ?? null;
      // Keep the previous state identity when nothing changed: every caret
      // move fires this, and a fresh [] each time would re-render the whole
      // rail per keystroke.
      setActiveRevisions((prev) =>
        prev.length === revisions.length &&
        revisions.every((revision, index) => revision === prev[index])
          ? prev
          : revisions
      );
      setActiveInlineRevisions(editor, revisions);
    },
    [editor]
  );
  const commitActiveRevision = useCallback(
    (revision: any) => commitActiveRevisions(revision ? [revision] : []),
    [commitActiveRevisions]
  );

  // Live revision count, for the new-edit fast path below.
  const lastRevisionCountRef = useRef(0);
  const revisionCount = useCallback(() => {
    const changes = editor?.revisions?.changes;
    if (Array.isArray(changes)) return changes.length;
    return editor?.revisions?.length ?? 0;
  }, [editor]);

  const refresh = useCallback(() => {
    const views = listRevisionGroups(editor);
    lastRevisionCountRef.current = revisionCount();
    // Neither a (re)load nor an assistant batch is something the user asked
    // to review, so nothing stays expanded through one.
    if (isOpeningDocument(editor) || isRobinEditing(editor)) {
      setExpanded({});
    }
    setGroups(
      views.map((view) => ({
        key: groupKeyOf(view.changeSetId, view.group),
        changeSetId: view.changeSetId,
        group: view.group,
        // A human view's "group" IS the author name; keep it verbatim. An engine card carries
        // its own title in the tag.
        title: view.untagged
          ? view.group
          : view.title ?? humanizeGroupId(view.group),
        ...(view.intent ? { intent: view.intent } : {}),
        engineOwned:
          !view.untagged && !!cardResolverFor(editor)?.owns(view.changeSetId),
        untagged: view.untagged,
        // Shown once in the group header instead of on every chip. Untagged
        // groups already show it as the title, so leave theirs unset.
        author: view.untagged ? undefined : view.items[0]?.author,
        derivedChanges: view.derivedChanges,
        chips: view.items.map((item) => ({
          revision: item.revision,
          revisions: item.revisions,
          partner: item.partner,
          partnerRevisions: item.partnerRevisions,
          revisionType: item.revisionType,
          text: item.text,
          beforeText: item.beforeText,
          author: item.author
        }))
      }))
    );
  }, [editor, revisionCount]);

  // contentChange fires once per keystroke. A change to the REVISION COUNT
  // (an edit arriving or resolving) lands in the rail immediately — the
  // inline wash and the selectionChange expansion already happened this
  // frame, and a card popping in a beat later reads as lag. Only text growth
  // inside an existing revision rides the trailing debounce. documentChange
  // (a DIFFERENT document opened in place) also rebuilds immediately.
  useEffect(() => {
    if (!editor) return;
    // EJ2 teardown race: a destroyed instance throws on any touch, so read it as "no editor".
    if (editor.isDestroyed) {
      setGroups([]);
      return;
    }
    // The read that reaches the host's boundary if it throws - and a destroy
    // racing this effect is not what that boundary is for.
    try {
      refresh();
    } catch (error) {
      if (!isTornDown(editor)) throw error;
      setGroups([]);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onContentChange = () =>
      handleEditorEvent(() => {
        clearTimeout(timer);
        // Every op changes the revision count, so refreshing immediately on
        // each one is O(n^2) across a batch whose intermediate state nobody
        // sees. Debounce instead: one refresh once the batch settles.
        const suppressed = isOpeningDocument(editor) || isRobinEditing(editor);
        if (!suppressed && revisionCount() !== lastRevisionCountRef.current)
          refresh();
        else timer = setTimeout(refresh, CONTENT_REFRESH_DEBOUNCE_MS);
      });
    const onDocumentChange = () => handleEditorEvent(refresh);
    editor.addEventListener?.('contentChange', onContentChange);
    editor.addEventListener?.('documentChange', onDocumentChange);
    return () => {
      clearTimeout(timer);
      // EJ2 teardown race: destroy can land between this check and the
      // removeEventListener calls, so those need their own guard too.
      if (editor.isDestroyed) return;
      try {
        editor.removeEventListener?.('contentChange', onContentChange);
        editor.removeEventListener?.('documentChange', onDocumentChange);
      } catch {
        // Torn down mid-unsubscribe: nothing left to detach from.
      }
    };
  }, [editor, refresh, revisionCount]);

  // Inline click → rail navigation via the native cursor→revision mapping.
  // Programmatic chip focus sets ignoreSelectionRef so its selectionChange
  // echo cannot re-land on a neighbouring edit.
  useEffect(() => {
    // EJ2 teardown race: subscribing on a destroyed instance throws.
    if (!editor || editor.isDestroyed) return;
    const onSelectionChange = () =>
      handleEditorEvent(() => {
        if (ignoreSelectionRef.current) return;
        // Same window refresh() collapses for. Without this, each op expands
        // whichever group it lands on and refresh() collapses it right back.
        if (isOpeningDocument(editor) || isRobinEditing(editor)) return;
        const current = editor.selection?.getCurrentRevision?.();
        const revisions: any[] = Array.isArray(current)
          ? current
          : current
          ? [current]
          : [];
        if (revisions.length) {
          for (const view of listRevisionGroups(editor)) {
            for (const item of view.items) {
              // Either half of a replace counts as clicking that one edit.
              if (!itemRevisions(item).some((rev) => revisions.includes(rev)))
                continue;
              const key = groupKeyOf(view.changeSetId, view.group);
              setExpanded((prev) =>
                prev[key] ? prev : { ...prev, [key]: true }
              );
              commitActiveRevision(item.revision);
              // An inline click is an explicit ask for the review panel.
              onHiddenChange?.(false);
              return;
            }
          }
        }
        // The cursor is not on an assistant edit: nothing is active.
        commitActiveRevision(null);
      });
    editor.addEventListener?.('selectionChange', onSelectionChange);
    return () => {
      // EJ2 teardown race: the destroy can land between this check and the
      // removeEventListener call, so isDestroyed alone isn't enough.
      if (!editor.isDestroyed) {
        try {
          editor.removeEventListener?.('selectionChange', onSelectionChange);
        } catch {
          // Torn down mid-unsubscribe: nothing left to detach from.
        }
      }
      activeRevisionRef.current = null;
      setActiveInlineRevisions(editor, []);
    };
  }, [editor, commitActiveRevision, onHiddenChange]);

  // Scroll only the rail's own scrollbox: scrollIntoView walks every
  // scrollable ancestor and would yank the whole form viewport.
  //
  // This effect has no dep array (it must run after the DOM settles), so it
  // fires on EVERY render — including a plain expand/collapse, which changes no
  // selection. Gate on the primary revision actually changing so toggling a
  // group open/closed leaves the rail scroll exactly where it was.
  useEffect(() => {
    const primary = activeRevisions[0];
    if (primary === lastScrolledPrimaryRef.current) return;
    lastScrolledPrimaryRef.current = primary ?? null;
    if (!primary) return;
    const row = rowRefs.current.get(primary);
    const box = scrollBoxRef.current;
    if (!row || !box || box.scrollHeight <= box.clientHeight) return;
    const offsetTop = (el: HTMLElement) =>
      el.getBoundingClientRect().top -
      box.getBoundingClientRect().top +
      box.scrollTop;
    // When the active chip is its group's FIRST, scrolling the chip row to the
    // top hides the group header just above it. Align to the card's top so the
    // heading of the change you clicked stays in view.
    const ownerGroup = groups.find(
      (mem) =>
        mem.chips[0] &&
        (mem.chips[0].revision === primary || mem.chips[0].partner === primary)
    );
    const card = ownerGroup && groupRefs.current.get(ownerGroup.key);
    const topTarget = card ?? row;
    const revealTop = offsetTop(topTarget);
    const rowBottom = offsetTop(row) + row.offsetHeight;
    if (revealTop < box.scrollTop) box.scrollTop = revealTop;
    else if (rowBottom > box.scrollTop + box.clientHeight)
      box.scrollTop = rowBottom - box.clientHeight;
  });

  // Each native accept/reject moves the selection, firing a real
  // selectionChange — one unguarded rail rescan per revision without this.
  const suppressingSelectionEcho = <T,>(fn: () => T): T => {
    ignoreSelectionRef.current = true;
    try {
      return fn();
    } finally {
      queueMicrotask(() => {
        ignoreSelectionRef.current = false;
      });
    }
  };

  const revisionIdsForAcceptance = (
    revisions: any[],
    beforeSfdt: string
  ): string[] => {
    const ids = revisions
      .map((revision) => revision?.revisionID ?? revision?.revisionId)
      .filter((id): id is string => typeof id === 'string' && !!id);
    if (ids.length) return Array.from(new Set(ids));

    // Older Syncfusion builds did not expose revisionID on the live wrapper.
    // Match the SFDT metadata in that case so confirmation versions still work.
    const keys = new Set(
      revisions.map((revision) =>
        JSON.stringify([
          revision?.author ?? '',
          revision?.revisionType ?? '',
          revision?.customData ?? ''
        ])
      )
    );
    const doc = JSON.parse(beforeSfdt);
    return Array.from(
      new Set<string>(
        (doc.revisions ?? [])
          .filter((revision: any) =>
            keys.has(
              JSON.stringify([
                revision?.author ?? '',
                revision?.revisionType ?? '',
                revision?.customData ?? ''
              ])
            )
          )
          .map((revision: any) => String(revision.revisionId ?? ''))
          .filter(Boolean)
      )
    );
  };

  const resolveWithHistory = async (
    revisions: any[],
    isAccept: boolean,
    resolve: () => void
  ) => {
    if (resolvingRef.current) return;
    resolvingRef.current = true;
    try {
      if (isAccept && onAcceptTrackedChanges) {
        const beforeSfdt = editor.serialize();
        await onAcceptTrackedChanges(
          {
            beforeSfdt,
            revisionIds: revisionIdsForAcceptance(revisions, beforeSfdt)
          },
          () => suppressingSelectionEcho(resolve)
        );
      } else {
        suppressingSelectionEcho(resolve);
      }
      refresh();
      // Resolving the last edit unmounts the rail — focus would land on
      // <body>, where nobody sees the next ⌘Z.
      if (listRevisionGroups(editor).length) refocusPanel();
      else editor?.focusIn?.();
    } finally {
      resolvingRef.current = false;
    }
  };
  // Every resolve path reports through here, so a stall can never be surfaced
  // by one entry point and swallowed by another.
  const reportStall = (
    unresolved: number,
    resolved: number,
    isAccept: boolean
  ) =>
    setStall(unresolved ? stallNotice(unresolved, resolved, isAccept) : null);

  // The editor's own accept or reject, with the document's table of contents refreshed after it in
  // the same undo, for every reviewer (a refresh that fails is logged and skipped).
  const settled = <T,>(resolve: () => T): T => withTocRefresh(editor, resolve);

  // Non-cascading resolve (native accept/reject settles whatever is
  // CONTIGUOUS, not the group), wrapped as ONE undo step.
  const resolveChips = (chips: ChipView[], isAccept: boolean) => {
    if (!chips.length) return Promise.resolve();
    const revisions = chips.flatMap(chipRevisions).filter(Boolean);
    return resolveWithHistory(revisions, isAccept, () => {
      const outcome = settled(() =>
        resolveRevisionsAsOneUndo(editor, revisions, isAccept)
      );
      reportStall(
        outcome?.unresolved?.length ?? 0,
        outcome?.length ?? 0,
        isAccept
      );
    });
  };

  // A card the document engine owns (structural, or one the editor's own accept or reject cannot
  // settle) is resolved by the engine as a whole; every other card natively, as before.
  const resolveGroups = (groupViews: GroupView[], isAccept: boolean) => {
    const revisions = groupViews.flatMap((group) =>
      group.chips.flatMap(chipRevisions)
    );
    return resolveWithHistory(revisions, isAccept, () => {
      const resolver = cardResolverFor(editor);
      const owned = groupViews.filter(
        (g) => !g.untagged && resolver?.owns(g.changeSetId)
      );
      const native = groupViews.filter((g) => !owned.includes(g));
      let outcome = null as ReturnType<
        typeof resolveLiveRevisionGroupsAsOneUndo
      > | null;
      const resolveNative = () => {
        outcome = settled(() =>
          resolveLiveRevisionGroupsAsOneUndo(editor, native, isAccept)
        );
      };
      let ownedResolved = true;
      // A batch with engine-owned cards resolves the rest alongside them, as one undo step
      if (owned.length)
        ownedResolved = !!resolver?.resolve(
          owned.map((g) => g.changeSetId),
          isAccept,
          native.length ? resolveNative : undefined
        );
      else if (native.length) resolveNative();
      const unresolvedOwned = ownedResolved
        ? 0
        : groupViews.reduce((n, g) => n + g.chips.length, 0);
      reportStall(
        (outcome?.unresolved?.length ?? 0) + unresolvedOwned,
        outcome?.length ?? 0,
        isAccept
      );
    });
  };

  // Accept all / Reject all: the same resolve, but a big batch blocks long
  // enough to be worth a spinner. See afterNextPaint.
  const resolveAllWithSpinner = (
    groupViews: GroupView[],
    isAccept: boolean
  ) => {
    if (resolvingAll) return;
    setResolvingAll(isAccept ? 'accept' : 'reject');
    afterNextPaint(() => {
      resolveGroups(groupViews, isAccept)
        .catch(reportEditorEventError)
        .finally(() => setResolvingAll(null));
    });
  };

  // Suppresses selectRevision's selectionChange echo (sync + trailing
  // microtask) so it can't reassign activeRevisions, then moves the cursor.
  const selectAndScrollToRevision = (revision: any) =>
    suppressingSelectionEcho(() => {
      // skipGroupSelect keeps navigation on this exact revision — the public
      // revision.select() may expand to the adjacent same-author/type group.
      const selection = editor?.selectionModule;
      if (typeof selection?.selectRevision === 'function') {
        selection.selectRevision(revision, undefined, undefined, true);
        // Explicit scroll too: some host/layout combos suppress the implicit
        // one while focus stays in the rail. Scroll to the START of the edit
        // (both args = start) so a long edit lands with its beginning in view,
        // not its tail.
        if (selection.start) {
          editor.documentHelper?.scrollToPosition?.(
            selection.start,
            selection.start
          );
        }
        // selectRevision leaves the whole edit SELECTED, so Syncfusion paints
        // its selection shading over our revision wash and it reads darker than
        // the same edit clicked in the document (a caret, no range). Collapse
        // to the start so only our wash shows — matching an in-document click.
        const at = selection.startOffset;
        if (typeof at === 'string' && typeof selection.select === 'function') {
          selection.select(at, at);
        }
      } else {
        revision?.select?.();
      }
    });

  const focusChip = (chip: ChipView) => {
    const group = groups.find((mem) => mem.chips.includes(chip));
    if (group) {
      setExpanded((prev) =>
        prev[group.key] ? prev : { ...prev, [group.key]: true }
      );
    }
    commitActiveRevision(chip.revision);
    selectAndScrollToRevision(chip.revision);
    refocusPanel();
  };

  // Group-title click: navigate to the group's first edit a person can see
  // (not a page break or an empty paragraph mark) but ring every chip, and
  // never expand — that's the caret's job alone.
  const focusGroupFirst = (group: GroupView) => {
    if (!group.chips.length) return;
    commitActiveRevisions(group.chips.flatMap(chipRevisions));
    selectAndScrollToRevision(firstVisibleChip(group.chips).revision);
    refocusPanel();
  };

  const allChips = groups.flatMap((group) => group.chips);

  // Report the pending count to the host (drives the toolbar's Changes badge).
  // Via a ref so an inline callback prop cannot re-fire the effect.
  const onPendingCountChangeRef = useRef(onPendingCountChange);
  onPendingCountChangeRef.current = onPendingCountChange;
  useEffect(() => {
    onPendingCountChangeRef.current?.(allChips.length);
  }, [allChips.length]);

  // O(1) chip lookups: a group-title click puts a whole group's revisions in
  // here, and every chip of every card checks membership per render.
  const activeRevisionSet = useMemo(
    () => new Set(activeRevisions),
    [activeRevisions]
  );

  const onPanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const key = event.key.toLowerCase();
    // The rail holds focus for J/K/A/R, so Syncfusion never sees undo/redo
    // chords pressed here — forward them to the document history.
    if ((event.ctrlKey || event.metaKey) && !event.altKey) {
      if (key === 'z' || key === 'y') {
        event.preventDefault();
        event.stopPropagation();
        // the same routing as the document and the toolbar (decision D4)
        if (key === 'y' || event.shiftKey) redoDocument(editor);
        else undoDocument(editor);
      }
      return;
    }
    if (event.altKey) return;
    if (
      key === 'j' ||
      key === 'arrowdown' ||
      key === 'k' ||
      key === 'arrowup'
    ) {
      if (!allChips.length) return;
      event.preventDefault();
      event.stopPropagation();
      const direction = key === 'j' || key === 'arrowdown' ? 1 : -1;
      const current = activeRevisionRef.current;
      const currentIndex = allChips.findIndex(
        (chip) => chip.revision === current || chip.partner === current
      );
      const nextIndex =
        currentIndex < 0
          ? direction > 0
            ? 0
            : allChips.length - 1
          : (currentIndex + direction + allChips.length) % allChips.length;
      // focusChip hands focus back to the rail after Syncfusion's selection
      // steals it, so the next J/K/arrow key stays with this handler.
      focusChip(allChips[nextIndex]);
      return;
    }
    if (key !== 'a' && key !== 'r') return;
    const current = activeRevisionRef.current;
    const focused = allChips.find(
      (chip) => chip.revision === current || chip.partner === current
    );
    if (!focused) return;
    event.preventDefault();
    event.stopPropagation();
    // an engine card has no per-edit resolution: A or R settles the card
    const owner = groups.find((g) => g.chips.includes(focused));
    if (owner?.engineOwned) resolveGroups([owner], key === 'a');
    else resolveChips([focused], key === 'a');
  };

  if (!groups.length) return null;

  return (
    <div
      css={{
        position: 'relative',
        flex: '0 0 auto',
        // Both children are absolutely anchored, so the rail contributes no
        // height of its own to the shared flex row: the document pane alone
        // sets the row height, and the stretched rail matches it exactly —
        // it can never hang below the editor and scroll past it.
        alignSelf: 'stretch',
        width: hidden ? 0 : 341,
        minHeight: 0
      }}
    >
      {onHiddenChange && hidden && (
        <BookmarkTab onExpand={() => onHiddenChange(false)} />
      )}
      {!hidden && (
        <div
          ref={panelRef}
          aria-label='Assistant tracked changes'
          tabIndex={0}
          onKeyDown={(event) => handleEditorEvent(() => onPanelKeyDown(event))}
          css={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: PANEL,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            fontSize: 13,
            color: INK,
            outline: 'none',
            '&:focus-visible': {
              boxShadow: `inset 0 0 0 2px ${ACCENT_LINE}`
            }
          }}
        >
          <RailHead
            pendingCount={allChips.length}
            onHide={onHiddenChange ? () => onHiddenChange(true) : undefined}
            onResolveAll={(isAccept) => resolveAllWithSpinner(groups, isAccept)}
            resolvingAll={resolvingAll}
            notice={stall}
          />
          <div
            ref={scrollBoxRef}
            css={{
              overflowY: 'auto',
              padding: '12px 12px 40px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              flex: 1,
              minHeight: 0
            }}
          >
            {groups.map((mem) => (
              <GroupCard
                key={mem.key}
                group={mem}
                cardRef={(el) => {
                  if (el) groupRefs.current.set(mem.key, el);
                  else groupRefs.current.delete(mem.key);
                }}
                isOpen={!!expanded[mem.key]}
                onToggle={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [mem.key]: !prev[mem.key]
                  }))
                }
                onNavigateFirst={() =>
                  handleEditorEvent(() => focusGroupFirst(mem))
                }
                activeRevisions={activeRevisionSet}
                chipRef={(chip) => (el) => {
                  if (el) rowRefs.current.set(chip.revision, el);
                  else rowRefs.current.delete(chip.revision);
                }}
                onFocusChip={(chip) => handleEditorEvent(() => focusChip(chip))}
                onResolveGroup={(isAccept) =>
                  handleEditorEvent(() => resolveGroups([mem], isAccept))
                }
                onResolveChips={(chips, isAccept) =>
                  handleEditorEvent(() => resolveChips(chips, isAccept))
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrackedChangeGroups;
